import { useState, ChangeEvent } from "react"
import { TextField, makeStyles } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/free-solid-svg-icons"

import { TextBoxOptionProps } from "../../../../utils/safetyPlanUtils"
import { colors } from "../../../../utils/theme"
import useSafetyPlanOptionStyles from "./useSafetyPlanOptionStyles"

/**
 * A free-text input option within the safety plan editor.
 *
 * In the default (client) mode, renders a single text field that calls `onChange`
 * on every keystroke. When `allowMultiple` is true (practitioner mode), renders
 * a list of text fields with an "Add" button that appends additional rows, and
 * calls `onChange` with the full array of values on each change.
 *
 * @param option - The `ResponseOption` this input corresponds to (used for its `id`).
 * @param type - The category type string passed back through `onChange`.
 * @param onChange - Callback invoked with `(id, values[], type)` on each change.
 * @param allowMultiple - When true, renders an "Add" button for multiple entries.
 */
export default function TextBoxOption({
  option,
  type,
  onChange,
  allowMultiple = false,
}: TextBoxOptionProps) {
  const [textboxes, setTextboxes] = useState([{ id: 0, value: "" }])
  const [nextId, setNextId] = useState(1)
  const [addError, setAddError] = useState<string | null>(null)

  const handleAddTextbox = () => {
    const last = textboxes[textboxes.length - 1]
    const lastTrimmed = (last?.value ?? "").trim()

    if (lastTrimmed.length === 0) {
      setAddError("Enter a value before adding another.")
      return
    }

    setAddError(null)
    setTextboxes((prev) => [...prev, { id: nextId, value: "" }])
    setNextId((prev) => prev + 1)
  }

  const handleTextboxChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number
  ) => {
    const updated = textboxes.map((tb) =>
      tb.id === id ? { ...tb, value: event.target.value } : tb
    )
    setTextboxes(updated)

    // Clear the "Add" validation error once the last row becomes non-empty.
    if (addError) {
      const last = updated[updated.length - 1]
      if ((last?.value ?? "").trim().length > 0) setAddError(null)
    }

    onChange(
      option.id,
      updated.map((tb) => tb.value),
      type
    )
  }

  const sharedClasses = useSafetyPlanOptionStyles()
  const textBoxClasses = useStyles()
  const classes = { ...sharedClasses, ...textBoxClasses }

  if (!allowMultiple) {
    // Simple single-input mode (client-facing)
    return (
      <div id={option.id} className={classes.textBoxContainer}>
        <div className={classes.inputContainer}>
          <TextField
            id="text-box-option-input"
            label="Add your own"
            value={textboxes[0].value}
            onChange={(e) => handleTextboxChange(e, 0)}
            variant="outlined"
            placeholder=""
            style={{ width: "100%", background: colors.neutral.white }}
          />
        </div>
      </div>
    )
  }

  // Multi-input mode with Add button (practitioner-facing)
  return (
    <div id={option.id} className={classes.textBoxContainerMulti}>
      {textboxes.map((tb, index) => (
        <div className={classes.inputContainer} key={tb.id}>
          <TextField
            label="Add your own"
            value={tb.value}
            onChange={(e) => handleTextboxChange(e, tb.id)}
            variant="outlined"
            placeholder=""
            error={Boolean(addError) && index === textboxes.length - 1}
            helperText={
              Boolean(addError) && index === textboxes.length - 1
                ? addError
                : undefined
            }
            style={{ width: "100%", background: colors.neutral.white }}
          />
        </div>
      ))}
      <div className={classes.addTextBoxContainer}>
        <button
          onClick={handleAddTextbox}
          className={classes.addTextBoxBtn}
          aria-label="Add another entry"
        >
          <FontAwesomeIcon icon={faPlus} style={{ color: "#FFF" }} />
          <div className={classes.addTextBoxTxt}>Add</div>
        </button>
      </div>
    </div>
  )
}

const useStyles = makeStyles(() => ({
  inputContainer: {
    display: "flex",
    padding: "4px 0px 4px 16px",
    alignItems: "flex-start",
    flex: "1 0 0",
    alignSelf: "stretch",
    borderRadius: "4px 4px 0px 0px",
  },
  textBoxContainerMulti: {
    display: "flex",
    flexDirection: "column",
    padding: "16px",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "4px",
  },
  addTextBoxContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    gap: "8px",
    alignSelf: "stretch",
  },
  addTextBoxBtn: {
    display: "flex",
    height: "32px",
    padding: "4px 20px",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    borderRadius: "200px",
    background: "#4F66AA",
    cursor: "pointer",
    border: "none",
  },
  addTextBoxTxt: {
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Lato",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
  },
}))
